import React from 'react';
import { useTranslation } from 'react-i18next';
import { InvoiceEditData } from '../../../../hooks/useDocumentEdit';

interface EditableInvoiceExtendedFieldsProps {
  data: InvoiceEditData;
  onUpdate: (field: keyof InvoiceEditData, value: any) => void;
  accentColor?: string;
}

/**
 * Composant pour les champs étendus des factures
 * Conforme Prisma: title, termsConditions
 * Permet d'ajouter un titre et des conditions générales aux factures
 */
export const EditableInvoiceExtendedFields: React.FC<EditableInvoiceExtendedFieldsProps> = ({
  data,
  onUpdate,
  accentColor = '#0d9488',
}) => {
  const { t } = useTranslation();
  return (
    <div className="invoice-extended-fields">
      {/* Informations complémentaires */}
      <div className="extended-section">
        <h3 style={{ color: accentColor }}>{t('templates.extendedFields.additionalInfo')}</h3>

        <div className="form-field">
          <label>{t('templates.extendedFields.documentTitle')}</label>
          <input
            type="text"
            value={data.title || ''}
            onChange={(e) => onUpdate('title', e.target.value)}
            placeholder={t('templates.extendedFields.documentTitlePlaceholder')}
            style={{ borderColor: accentColor + '40' }}
          />
        </div>

        {/* S6 — Toggle Autoliquidation TVA BTP */}
        <div className="form-field" style={{ flexDirection: 'row', alignItems: 'center', gap: '0.75rem' }}>
          <input
            type="checkbox"
            id="isReverseCharge"
            checked={data.isReverseCharge || false}
            onChange={(e) => onUpdate('isReverseCharge', e.target.checked)}
            style={{ width: 18, height: 18, accentColor }}
          />
          <label htmlFor="isReverseCharge" style={{ marginBottom: 0, cursor: 'pointer' }}>
            {t('templates.extendedFields.reverseCharge')}
          </label>
        </div>
        {data.isReverseCharge && (
          <div style={{ background: '#fef3c7', borderRadius: 8, padding: '0.5rem 0.75rem', fontSize: '0.8rem', color: '#92400e', marginBottom: '1rem' }}>
            {t('templates.extendedFields.reverseChargeNotice')}
          </div>
        )}

        <div className="form-field">
          <label>{t('templates.extendedFields.termsConditions')}</label>
          <textarea
            value={data.termsConditions || ''}
            onChange={(e) => onUpdate('termsConditions', e.target.value)}
            rows={4}
            placeholder={t('templates.extendedFields.termsPlaceholder')}
            style={{ borderColor: accentColor + '40' }}
          />
        </div>
      </div>

      <style>{`
        .invoice-extended-fields {
          margin-top: 2rem;
          padding-top: 2rem;
          border-top: 2px solid #e9ecef;
        }

        .extended-section {
          margin-bottom: 2rem;
        }

        .extended-section h3 {
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 1rem;
          padding-bottom: 0.5rem;
          border-bottom: 1px solid #e9ecef;
        }

        .form-field {
          display: flex;
          flex-direction: column;
          margin-bottom: 1rem;
        }

        .form-field label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #495057;
          margin-bottom: 0.5rem;
        }

        .form-field input,
        .form-field textarea {
          padding: 0.625rem;
          border: 1px solid #ced4da;
          border-radius: 4px;
          font-size: 0.875rem;
          transition: border-color 0.2s;
        }

        .form-field input:focus,
        .form-field textarea:focus {
          outline: none;
          border-color: currentColor;
          box-shadow: 0 0 0 3px currentColor10;
        }

        .form-field textarea {
          resize: vertical;
          font-family: inherit;
        }
      `}</style>
    </div>
  );
};
